import { FC, useEffect, useState } from 'react'
import { Checkbox } from "../../../../base/Checkbox"
import addressIcon from "../../../../../assets/fonts/Location-marker.svg"
import "./styles.scss"
import { Panel } from "../../../Panel"
import { Select } from "../../../Select"
import { Textarea } from "../../../Textarea"
import { FormikType } from "../../../../../types/VisitReports/types"
import { VISITS_ADDRESS } from "../../../../../constants/CreateHousehold"
import { Input } from '../../../../base/Input';
import {FormErrorMessage} from '../../../../base/FormErrorMessage';
import { PlaceAutocompleteInput, LegacyPlace } from '../../../../base/PlaceAutocompleteInput';
import { ManualAddressSection } from '../../../../base/ManualAddressSection';
import { manualCoordinateInlineErrors } from '../../../../../validationSchemas/manualAddressRules';
import { visitAddressHasContent } from '../../../../../validationSchemas/visitAddressRules';
import { useTranslation } from '../../../../../hooks/useTranslation'
import { useContactEnumLabel } from '../../../../../utils/contactEnumDisplay'
import { useSelector } from 'react-redux'
import { deriveManualDefaults } from '../../../../../utils/departureDefaults'
import { TextOnlyAddressConfirmDialog } from '../../../../base/TextOnlyAddressConfirmDialog'
import { TextOnlyAddressBadge } from '../../../../base/TextOnlyAddressBadge'
import { extractAddressFields } from "../../../../../utils/placeComponents";

type Props = {
  formik: FormikType
  addressesTypes: string[]
  openLeftTown: boolean
  setOpenLeftTown: (value: boolean) => void,
  showCheckbox?: boolean
  // The household's contacts — used to pick which contact a WORK address belongs to.
  contacts?: { id: number; full_name: string }[]
  // The household's current house phone, shown when "Edit Home details" is toggled (only when
  // the viewer may read contact info).
  currentHousePhone?: string
  // The household's current home address, shown for reference under "Edit Home details".
  currentHomeAddress?: any
  canViewContactInfo?: boolean
  // Whether to render the "Edit Home details" toggle. Hidden in the notification context where
  // the staged house phone is surfaced separately and editing here is not appropriate.
  showEditHomeDetails?: boolean
  // When the staged address is ALWAYS the household home (e.g. a contact-info notification that
  // carries a non-left-town home change), lock it to a Home address: hide the Home/Work Type
  // selector entirely — a house is always Home — and never show the Work contact picker.
  lockHomeType?: boolean
  // Which formik field this editor reads/writes. Defaults to `new_address` (Left Town — the
  // household MOVED). The admin review UI for a "Update contact info" home-address CORRECTION
  // (household did NOT move) passes `updated_home_address` instead — a fully independent field/
  // backend column, so editing one can never touch the other.
  fieldName?: string
}

export const LeftTown: FC<Props> = ({
  formik,
  addressesTypes,
  openLeftTown,
  setOpenLeftTown,
  showCheckbox,
  contacts = [],
  lockHomeType = false,
  fieldName = 'new_address',
}) => {
  const { p } = useTranslation('contact_info');
  const enumLabel = useContactEnumLabel();
  const workspace = useSelector((state: any) => state.authStore?.workspace);
  const { state: defaultState, country: defaultCountry } = deriveManualDefaults(workspace);

  const fieldValue = (formik.values as any)?.[fieldName];
  const fieldTouched = (formik.touched as any)?.[fieldName];
  const fieldErrors = (formik.errors as any)?.[fieldName];

  // Whether to draw the red "*" required markers.
  //
  // For "Left town" (`new_address`) the address is OPTIONAL — a worker often knows a
  // household moved but not where to — and only becomes required once they actually start
  // entering one, at which point it has to be completed. A permanent asterisk on an
  // optional field just reads as "you must fill this in", so the markers follow the real
  // rule instead: nothing is marked until an address is entered.
  //
  // The home-address CORRECTION reuse of this editor (`updated_home_address`, household did
  // NOT move) keeps its own always-required rule — see useVisitReportForm.
  const addressIsOptional = fieldName === 'new_address';
  const markRequired = !addressIsOptional || visitAddressHasContent(fieldValue);

  // Lock the staged address to Home so the (hidden) Type selector can never leave it as Work.
  useEffect(() => {
    if (lockHomeType && fieldValue && fieldValue.type !== 'Home') {
      formik.setFieldValue(`${fieldName}.type`, 'Home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockHomeType, fieldValue?.type]);
  // A visit reports either the household HOME address or a contact's WORK address.
  const addressTypeOptions = ['Home', 'Work'];
  const isWork = fieldValue?.type === 'Work';
  // Home defaults to primary (only explicit false opts out); Work is primary only when set true.
  const primaryChecked =
    fieldValue?.type === 'Home'
      ? fieldValue?.set_as_household_address !== false
      : !!fieldValue?.set_as_household_address;
  // Manual mode for the main "new address" entry below (the household/contact's new place).
  const newAddrManual = !!fieldValue?.is_manual;
  const newAddrTextOnly = !!fieldValue?.is_text_only;
  const [pendingTextOnly, setPendingTextOnly] = useState<string | null>(null);
  const confirmNewAddrTextOnly = () => {
    if (pendingTextOnly === null) return;
    formik.setFieldValue(fieldName, {
      ...fieldValue,
      is_text_only: true,
      is_manual: false,
      formatted_address: pendingTextOnly,
      fullAddress: pendingTextOnly,
      place_id: '', latitude: '', longitude: '',
      country: '', state: '', city: '', street_name: '', zip_code: '',
      apartment_number: '', unit_number: '',
      routing_latitude: '', routing_longitude: '',
    });
    setPendingTextOnly(null);
  };
  const setNewAddrField = (leaf: string, value: unknown) =>
    formik.setFieldValue(`${fieldName}.${leaf}`, value);
  // Coordinate errors for a MANUAL address, surfaced only after a submit attempt.
  const coordErrors = formik.submitCount > 0 ? (fieldErrors as any) : undefined;
  // Out-of-range coordinates show LIVE; the submit-gated schema error covers "missing".
  const liveCoordErrors = manualCoordinateInlineErrors(fieldValue);

  const handleLeftTown = (value: boolean) => {
    setOpenLeftTown(value)
    // Persist that this is an actual "Left town" report (household moved) so the approval
    // notification is titled/labelled "Left town" — as opposed to a plain "Edit Home details"
    // correction, which leaves this false and reads as "Home details updated".
    formik.setFieldValue("is_left_town", value)
    if (!openLeftTown) {
      formik.setFieldValue(fieldName, VISITS_ADDRESS)
    } else {
      //this syntax deleting field
      formik.setFieldValue(fieldName, undefined)
    }
  }

  const handleAddressType = async (value: string) => {
    await formik.setFieldValue(`${fieldName}.type`, value)
    // A Home address defaults to being the household's primary address. Persist that
    // default explicitly so the saved value matches the checkbox — otherwise it appears
    // checked but is stored as not-primary (set_as_household_address stays undefined).
    if (value === 'Home') {
      await formik.setFieldValue(`${fieldName}.set_as_household_address`, true)
    }
    await formik.setTouched({ ...formik.touched, [fieldName]: { ...(fieldTouched as any), type: true } })
  }

  const handleAddress = async (e: any) => {
    if(!e.target.value) return formik.setFieldValue(fieldName, {...VISITS_ADDRESS, type: fieldValue?.type});
    await formik.setFieldValue(`${fieldName}.fullAddress`, e.target.value);
    // Manual typing/editing invalidates any prior autocomplete selection — the user MUST pick
    // from the list again. Clearing place_id/coords makes the place_id-required validation block
    // the save (and the notification's confirm) until a fresh selection is made.
    await formik.setFieldValue(`${fieldName}.place_id`, '');
    await formik.setFieldValue(`${fieldName}.latitude`, '');
    await formik.setFieldValue(`${fieldName}.longitude`, '');
    // Editing the text exits a prior "save as text only" choice too, so it resumes the
    // normal Google-suggestions flow instead of silently staying flagged text-only.
    await formik.setFieldValue(`${fieldName}.is_text_only`, false);
    // Mark place_id touched so the "select an address from the list" error renders next to the
    // field (the main field's wrapper sets this too, but the home-address field has no wrapper).
    await formik.setFieldTouched(`${fieldName}.place_id`, true);
  }

  const handleAddressNote = (e: any) => {
    formik.setFieldValue(`${fieldName}.info_note`, e.target.value)
  }

  const handleCoordinates = (
      place: any,
  ) => {
    // Fallback selections (fetchFields failed) carry only place_id/formatted_address —
    // still record what we have so place_id-required validation can pass.
    const latitude = place.geometry?.location?.lat?.();
    const longitude = place.geometry?.location?.lng?.();
    // Shared extraction — matches a type ANYWHERE in a component's `types`, and falls back
    // past `locality` for the countries where Google does not return one. See
    // utils/placeComponents.ts for why the old types[0]/locality-only reads left City blank.
    const addressFields = extractAddressFields(place);
    const placeId = place.place_id;
    // A SINGLE merged setFieldValue call (not many un-awaited ones) — Formik schedules its own
    // async validation pass per setFieldValue call, keyed to the values snapshot AT THAT MOMENT;
    // firing a dozen of them back-to-back races those passes against each other, and a stale one
    // (still seeing place_id as cleared) can resolve LAST and overwrite a fresh, valid result —
    // leaving the "select an address" error stuck even though place_id is genuinely set. One
    // atomic update means exactly one validation pass against the fully merged, final data.
    formik.setFieldValue(fieldName, {
      ...fieldValue,
      ...addressFields,
      place_id: placeId,
      latitude,
      longitude,
      formatted_address: place.formatted_address || "",
      fullAddress: place.formatted_address || "",
      // A real Google pick always exits text-only mode (and manual, belt-and-suspenders).
      is_text_only: false,
      is_manual: false,
    });
  };


  const handlePlaceSelected = (place: LegacyPlace) => {
    if (!place.place_id) return;
    // `place.formatted_address` is already correctly localized (fetched with the
    // request's language) by PlaceAutocompleteInput.selectSuggestion — a redundant
    // geocodePlaceId() re-fetch here (with no language param) used to silently
    // overwrite a Hebrew-selected address with an English one.
    handleCoordinates(place);
  };

  return (
    <div className="left-town-report">
      {/* NOTE: the household HOME details (house phone + home address) used to live here behind
          an "Edit Home details" toggle. They now live in the "Update Contact Info" step
          (HomeDetailsBlock). "Left town" below is a separate, untouched flow — its checkbox
          sets is_left_town, which keeps its own approval notification. */}
      {showCheckbox && (
          <div className="left-town-report__checkbox">
            <Checkbox
                label={p('left_town')}
                checked={openLeftTown}
                onCheck={() => handleLeftTown(!openLeftTown)}
            />
          </div>
      )}
      <div
        className={`left-town-report__container ${
          !openLeftTown ? "left-town-report__container_closed" : ""
        }`}
      >
        <div className="left-town-report__panel">
          {/* This editor is reused for two distinct things: the Left Town "household MOVED" step
              (title "New address") and the Update-Contact-Info home-address CORRECTION, which is
              always locked to Home (`lockHomeType`) — for that one "New address" is misleading
              since the household did not move, so it reads "Home address" instead. */}
          <Panel icon={addressIcon} title={lockHomeType ? p('home_address') : p('new_address')}/>
        </div>
        {/* A house is always Home — hide the Type selector when locked to a home address. */}
        {!lockHomeType && (
          <div className="left-town-report__select">
            <Select
              isRequired={markRequired}
              onChangeClose
              label={p('type')}
              list={addressTypeOptions}
              getOptionLabel={enumLabel}
              value={fieldValue?.type}
              placeholder={p('select')}
              handleSelect={handleAddressType} isError={
                (fieldTouched as unknown as { type: boolean })?.type && !!(fieldErrors as unknown as { type: string })?.type
            }
            />
            {(fieldTouched as unknown as { type: boolean })?.type && (fieldErrors as unknown as { type: string })?.type && (
                <FormErrorMessage message={(fieldErrors as unknown as { type: string })['type']} />
            )}
          </div>
        )}
        {/* A WORK address belongs to a specific contact — choosing one is required. The
            `home-page-input` wrapper is required: its floating `home-page-input__label` is
            position:absolute, so without a position:relative parent it anchors to `.notification`
            and overlaps the title. */}
        {isWork && (
          <div className="left-town-report__select">
            <div className="home-page-input">
              <label className="home-page-input__label">
                {p('work_for_contact')}
                {markRequired && <span style={{ color: 'red' }}>*</span>}
              </label>
              <select
                className="home-page-input__input"
                value={fieldValue?.person_id ?? ''}
                onChange={(e: any) =>
                  formik.setFieldValue(
                    `${fieldName}.person_id`,
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                <option value="">{p('select_contact')}</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}
                  </option>
                ))}
              </select>
            </div>
            {!fieldValue?.person_id && !!fieldValue?.place_id && (
              <FormErrorMessage message={p('must_select_contact')} />
            )}
          </div>
        )}
        {/* Make this the household's primary address pointer. A Home address defaults to
            primary (the household's main address); a Work (office) address is primary only
            when explicitly chosen. Either can be opted out (e.g. keep a contact office as
            primary while still updating the home). */}
        {(fieldValue?.type === 'Home' || isWork) && (
          <div className="left-town-report__checkbox">
            <Checkbox
              label={p('primary_address')}
              checked={primaryChecked}
              onCheck={() =>
                formik.setFieldValue(`${fieldName}.set_as_household_address`, !primaryChecked)
              }
            />
          </div>
        )}
        {!newAddrManual && (
          <div className="left-town-report__input" onChange={() => {
            formik.setTouched({ ...formik.touched, [fieldName]: { ...(fieldTouched as any), place_id: true } })
          }}>
            <PlaceAutocompleteInput
                isRequired={markRequired}
                label={p('address')}
                value={fieldValue?.fullAddress || ""}
                placeholder={p('write_address')}
                includedPrimaryTypes={['street_address', 'premise', 'subpremise']}
                onChange={handleAddress}
                onPlaceSelected={handlePlaceSelected}
                textOnlyLabel={p('save_as_text_only')}
                onSaveAsTextOnly={(text) => setPendingTextOnly(text)}
                isError={(fieldTouched as unknown as { place_id: boolean })?.place_id &&
                !!(fieldErrors as unknown as { place_id: string })?.place_id}
            />
            {(fieldTouched as unknown as { place_id: boolean })?.place_id &&
                !!(fieldErrors as unknown as { place_id: string })?.place_id && (
                    <FormErrorMessage message={(fieldErrors as unknown as { place_id: string })?.place_id} />
                )}
            {newAddrTextOnly && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginTop: 4 }}>
                <TextOnlyAddressBadge isTextOnly />
              </div>
            )}
          </div>
        )}
        <TextOnlyAddressConfirmDialog
          open={pendingTextOnly !== null}
          addressText={pendingTextOnly || ''}
          onConfirm={confirmNewAddrTextOnly}
          onCancel={() => setPendingTextOnly(null)}
        />
        {/* Apartment is a structured column — hidden in manual mode (text + coordinates only). */}
        {!newAddrManual && !newAddrTextOnly && (
        <div className="left-town-report__input">
          <Input
            label={p('apartment_number')}
            placeholder={p('apartment_number')}
            value={fieldValue?.apartment_number || ''}
            onChange={(e: any) => {
              formik.setFieldValue(`${fieldName}.apartment_number`, e.target.value)
            }}
          />
        </div>
        )}

        {/* Manual-address toggle + (when on) editable text/coordinates/routing point. */}
        <ManualAddressSection
          value={fieldValue || {}}
          setField={setNewAddrField}
          p={p}
          defaultCountry={defaultCountry}
          defaultState={defaultState}
          latitudeError={liveCoordErrors.latitude || coordErrors?.latitude}
          longitudeError={liveCoordErrors.longitude || coordErrors?.longitude}
        />
        <div>
          <Textarea
            label={p('address_note')}
            placeholder={p('write_some_note')}
            value={fieldValue?.info_note}
            onChange={handleAddressNote}
          />
        </div>
      </div>
    </div>
  )
}
